import AsyncSelect from "react-select/async";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Modal, ModalBody, ModalHeader, Row } from "react-bootstrap";
import { useSelector } from "react-redux";

import Icon from "../../../components/Icon";
import SiteTitle from "../../../helpers/SiteTitle/SiteTitle";
import TableFilter from "../../../components/TableFilter";
import SearchTable from "../../../components/SearchTable";
import EmptyData from "../../../components/EmptyData";
import UserAvatar from "../../../components/UserAvatar";
import PaginationComponent from "../../../components/Pagination";
import Currency, {
  ConvertCurrency,
  ConvertCurrencyToText,
} from "../../../helpers/Currency/Currency";
import Badge from "../../../components/Badge";
import ErrorMessage from "../../../components/ErrorMessage";
import InfoItem from "../../../helpers/InfoItem/InfoItem";
import DateTimePicker from "../../../components/DateTimePicker";
import PaymentGateway from "../../../components/PaymentGateway";
import DataExporter from "../../../components/DataExporter";
import DocumentsSection, {
  DocumentsDetails,
} from "../../../components/DocumentsSection";
import { invoiceUserContext } from "../../../context/UserContext/InvoiceUserContext/InvoiceUserContext";
import { createInvoiceValidation } from "../../../utils/validation/validation";
import {
  convertDate,
  numberSeparate,
  removeNumberSeparate,
} from "../../../utils";
import {
  convertRoleToPath,
  convertInvoiceTypeToText,
  convertInvoiceStatusToText,
  convertInvoiceStatusToColor,
  invoiceStatusItems,
  invoiceTypeItems,
  exporterData,
} from "../../../utils/enum";
import {
  areYouSureMessage,
  toastErrorMessage,
} from "../../../utils/toastMessage/toastMessage";

const InvoicesUser = () => {
  const navigate = useNavigate();

  const { user: userId } = useParams();

  const loader = useSelector((state) => state.loader);
  const auth = useSelector((state) => state.auth);

  const {
    invoices,
    invoice,
    setInvoice,
    filter,
    setFilter,
    sort,
    setSort,
    type,
    setType,
    status,
    setStatus,
    start,
    setStart,
    end,
    setEnd,
    pageCount,
    pageNumber,
    setPageNumber,
    itemsCount,
    setItemsCount,
    isShowModal,
    handleShowModal,
    handleHideModal,
    handleGetUsers,
    handleGetInvoices,
    handleCreateInvoice,
    handleEditInvoice,
    handleDeleteInvoice,
  } = useContext(invoiceUserContext);

  useEffect(() => {
    handleGetInvoices();
  }, [start, end, pageNumber, type, status, itemsCount, sort, userId]);

  // Validation
  const {
    control,
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(createInvoiceValidation),
  });

  return (
    <>
      <SiteTitle title="فاکتورها" />

      <TableFilter
        setItemsCount={setItemsCount}
        itemsCount={itemsCount}
        setSort={setSort}
        sort={sort}
        setStart={setStart}
        start={start}
        setEnd={setEnd}
        end={end}
        invoiceStatus={status}
        setInvoiceStatus={setStatus}
        invoiceType={type}
        setInvoiceType={setType}
      />

      <div className="card h-100 p-0 radius-12">
        <SearchTable
          endItem={
            <div className="d-flex justify-content-between align-items-center gap-2">
              {!isEmpty(invoices) && (
                <DataExporter
                  headers={exporterData.invoices.headers}
                  keys={exporterData.invoices.keys}
                  data={invoices}
                  fileName={exporterData.invoices.fileName}
                />
              )}
              {auth.isAdmin && (
                <button
                  className="btn btn-primary text-sm btn-sm radius-8 d-flex align-items-center gap-2"
                  onClick={() => {
                    handleShowModal();
                    reset();
                  }}
                >
                  <Icon icon="ic:baseline-plus" className="text-xl" />
                  افزودن فاکتور
                </button>
              )}
            </div>
          }
          filter={filter}
          setFilter={setFilter}
          onSearch={handleGetInvoices}
        />
        <div className="card-body p-24">
          {isEmpty(invoices) ? (
            <EmptyData />
          ) : (
            <>
              <div className="table-responsive scroll-sm">
                <table className="table bordered-table sm-table mb-0">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">شماره</th>
                      {auth.isAdmin && (
                        <>
                          <th scope="col">کاربر</th>
                          <th scope="col">شماره همراه</th>
                        </>
                      )}
                      <th scope="col">
                        مبلغ <Currency />
                      </th>
                      <th scope="col">نوع</th>
                      <th scope="col">وضعیت</th>
                      <th scope="col">تاریخ انقضا</th>
                      <th scope="col">تاریخ ایجاد</th>
                      <th scope="col">تعداد اسناد</th>
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((item, index) => {
                      const isPay =
                        item.amount > 0 &&
                        item.status === invoiceStatusItems[0];

                      const prefixClass = isPay ? "bg-warning-100" : "";
                      return (
                        <tr key={item._id}>
                          <td className={prefixClass}>{index + 1}</td>
                          <td className={prefixClass}>{item?.number}</td>
                          {auth.isAdmin && (
                            <>
                              <td className={prefixClass}>
                                <div className="d-flex align-items-center">
                                  <UserAvatar
                                    className="ms-2"
                                    {...(item?.user?.avatar
                                      ? { image: item?.user?.avatar }
                                      : {
                                          icon: "solar:user-broken",
                                        })}
                                  />
                                  <div className="flex-grow-1">
                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                      {item?.user?.firstName}{" "}
                                      {item?.user?.lastName}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className={prefixClass}>
                                {item?.user?.phoneNumber}
                              </td>
                            </>
                          )}
                          <td className={prefixClass}>
                            <ConvertCurrency price={item.amount} pending />
                          </td>
                          <td className={prefixClass}>
                            {convertInvoiceTypeToText(item.type)}
                          </td>
                          <td className={prefixClass}>
                            <Badge
                              color={convertInvoiceStatusToColor(item.status)}
                            >
                              {convertInvoiceStatusToText(item.status)}
                            </Badge>
                          </td>
                          <td className={prefixClass}>
                            {convertDate(item.expiredAt)}
                          </td>
                          <td className={prefixClass}>
                            {convertDate(item.createdAt, "YYYY/MM/DD - HH:mm")}
                          </td>
                          <td className={prefixClass}>
                            {numberSeparate(item.documents?.length)}
                          </td>
                          <td className={prefixClass}>
                            <div className="dropdown">
                              <button
                                className="hover-bg-transparent"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                              >
                                <Icon
                                  icon="ph:dots-three-outline-fill"
                                  className="menu-icon"
                                />
                              </button>
                              <ul className="dropdown-menu list">
                                {isPay && (
                                  <li
                                    onClick={() => {
                                      setInvoice({ ...item, isPay: true });
                                    }}
                                    className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                  >
                                    پرداخت فاکتور
                                  </li>
                                )}
                                <li
                                  onClick={() =>
                                    navigate(
                                      `/${convertRoleToPath(
                                        auth.role
                                      )}/renewals/${item._id}/invoice`
                                    )
                                  }
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  گزارش تمدیدهای فاکتور
                                </li>

                                <li
                                  onClick={() =>
                                    navigate(
                                      `/${convertRoleToPath(
                                        auth.role
                                      )}/payments/${item._id}/invoice`
                                    )
                                  }
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  گزارش پرداخت های فاکتور
                                </li>
                                <li
                                  onClick={() =>
                                    navigate(
                                      `/${convertRoleToPath(
                                        auth.role
                                      )}/orders/${item._id}/invoice`
                                    )
                                  }
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  گزارش سفارش فاکتور
                                </li>
                                <li
                                  onClick={() => setInvoice(item)}
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                >
                                  جزئیات
                                </li>
                                {auth.isAdmin && (
                                  <>
                                    <li
                                      onClick={() => {
                                        reset();
                                        handleShowModal(item, setValue);
                                      }}
                                      className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer"
                                    >
                                      ویرایش
                                    </li>

                                    <li className="dropdown-divider" />

                                    <li
                                      onClick={() =>
                                        areYouSureMessage(
                                          () => handleDeleteInvoice(item._id),
                                          `آیا از حذف فاکتور ${item?.user?.firstName} ${item?.user?.lastName} مطمئنید؟`
                                        )
                                      }
                                      className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 cursor-pointer text-danger"
                                    >
                                      حذف
                                    </li>
                                  </>
                                )}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <PaginationComponent
                pageCount={pageCount}
                currentPage={pageNumber}
                setPageNumber={setPageNumber}
              />
            </>
          )}
        </div>
      </div>

      <Modal
        show={isShowModal}
        className="modal-dialog-centered"
        size="lg"
        onHide={handleHideModal}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">
            {isEmpty(invoice) ? "افزودن فاکتور جدید" : "ویرایش فاکتور"}
          </span>
        </ModalHeader>
        <ModalBody>
          <form
            autoComplete="off"
            onSubmit={handleSubmit(
              isEmpty(invoice) ? handleCreateInvoice : handleEditInvoice
            )}
          >
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="category"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      کاربر
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { onChange, onBlur, value } }) => (
                        <AsyncSelect
                          isDisabled={invoice.type === invoiceTypeItems[0]}
                          defaultOptions
                          openMenuOnFocus
                          cacheOptions
                          className="react-select-container"
                          classNamePrefix="react-select"
                          placeholder="لطفا کاربر را انتخاب نمایید"
                          isLoading={loader}
                          onBlur={onBlur}
                          noOptionsMessage={() => (
                            <p>کاربری برای نمایش یافت نشد</p>
                          )}
                          loadingMessage={() => <p>لطفا منتظر بمانید ...</p>}
                          loadOptions={(val, callback) => {
                            handleGetUsers(val, callback);
                          }}
                          onChange={onChange}
                          value={value}
                          isClearable
                        />
                      )}
                      name="user"
                    />
                  </div>

                  <ErrorMessage
                    message={
                      errors.user?.message || errors.user?.value?.message
                    }
                  />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="amount"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      مبلغ <Currency />
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <input
                          type="text"
                          placeholder="مبلغ را وارد نمایید"
                          className="form-control radius-8"
                          onChange={(e) => {
                            const value = removeNumberSeparate(e.target.value);
                            onChange(value);
                          }}
                          onBlur={onBlur}
                          value={numberSeparate(value)}
                          inputMode="decimal"
                        />
                      )}
                      name="amount"
                    />
                    <ConvertCurrencyToText price={watch("amount")} />
                  </div>

                  <ErrorMessage message={errors.amount?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="status"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      وضعیت
                    </label>
                    <select
                      className="form-control radius-8"
                      {...register("status")}
                    >
                      <option value="" selected hidden>
                        وضعیت را انتخاب نمایید
                      </option>
                      {invoiceStatusItems.map((item) => (
                        <option
                          key={item}
                          value={item}
                          selected={watch("status") === item}
                        >
                          {convertInvoiceStatusToText(item)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <ErrorMessage message={errors.status?.message} />
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="expiredAt"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      تاریخ انقضا
                    </label>
                    <Controller
                      control={control}
                      render={({ field: { value, onChange, onBlur } }) => (
                        <DateTimePicker
                          id="expiredAt"
                          name="expiredAt"
                          className="form-control radius-8"
                          placeholder="تاریخ انقضا را وارد نمایید"
                          onChange={onChange}
                          onBlur={onBlur}
                          initValue={value}
                        />
                      )}
                      name="expiredAt"
                    />
                  </div>

                  <ErrorMessage message={errors.expiredAt?.message} />
                </div>
              </div>
              <div className="col-sm-12">
                <div className="mb-20">
                  <div>
                    <label
                      htmlFor="description"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      توضیحات
                    </label>
                    <textarea
                      name="description"
                      className="form-control radius-8"
                      id="description"
                      rows={5}
                      placeholder="توضیحات را وارد نمایید"
                      {...register("description")}
                    />
                  </div>
                  <ErrorMessage message={errors.description?.message} />
                </div>
              </div>
              <DocumentsSection
                watch={watch}
                setValue={setValue}
                register={register}
                errors={errors}
              />
            </div>
            <div className="text-end mt-3">
              <button
                type="submit"
                className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
              >
                {isEmpty(invoice) ? "افزودن" : "ویرایش"} فاکتور
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <Modal
        show={!isEmpty(invoice) && !invoice.isEdit && !invoice.isPay}
        className="modal-dialog-centered"
        size="lg"
        onHide={() => setInvoice({})}
      >
        <ModalHeader closeButton>
          <span className="fs-5 fw-bold">جزئیات فاکتور</span>
        </ModalHeader>
        <ModalBody>
          {auth.isAdmin && (
            <>
              <span className="text-primary border-bottom fw-bold">
                اطلاعات کاربر
              </span>
              <Row className="g-2">
                <Col sm="6">
                  <InfoItem title="نام">{invoice?.user?.firstName}</InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="نام خانوادگی">
                    {invoice?.user?.lastName}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="شماره همراه">
                    {invoice?.user?.phoneNumber}
                  </InfoItem>
                </Col>
                <Col sm="6">
                  <InfoItem title="پست الکترونیک">
                    {invoice?.user?.email}
                  </InfoItem>
                </Col>
              </Row>
            </>
          )}
          <InvoiceDetails invoice={invoice} />
        </ModalBody>
      </Modal>

      <PayInvoice />
    </>
  );
};

export default InvoicesUser;

export const InvoiceDetails = ({ invoice }) => {
  return (
    <>
      <span className="text-primary border-bottom fw-bold">اطلاعات فاکتور</span>
      <Row className="g-2">
        <Col sm="6">
          <InfoItem title="شماره">{invoice?.number}</InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="مبلغ ">
            <ConvertCurrency price={invoice?.amount} pending /> <Currency />
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="وضعیت">
            <Badge color={convertInvoiceStatusToColor(invoice?.status)}>
              {convertInvoiceStatusToText(invoice?.status)}
            </Badge>
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="نوع">
            {convertInvoiceTypeToText(invoice?.type)}
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="تاریخ انقضا">
            {convertDate(invoice?.expiredAt)}
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="تاریخ ایجاد">
            {convertDate(invoice?.createdAt, "YYYY/MM/DD - HH:mm")}
          </InfoItem>
        </Col>
        <Col sm="6">
          <InfoItem title="تاریخ آخرین ویرایش">
            {convertDate(invoice?.updatedAt, "YYYY/MM/DD - HH:mm")}
          </InfoItem>
        </Col>
        <Col xs="12">
          <InfoItem title="توضیحات">{invoice?.description}</InfoItem>
        </Col>
      </Row>
      {!!invoice?.discount && (
        <>
          <span className="text-primary border-bottom fw-bold">
            اطلاعات تخفیف
          </span>
          <Row className="g-2">
            <Col sm="6">
              <InfoItem title="کد تخفیف">{invoice?.discount?.code}</InfoItem>
            </Col>
            <Col sm="6">
              <InfoItem title="مبلغ">
                <ConvertCurrency price={invoice?.discount?.amount} />{" "}
                <Currency />
              </InfoItem>
            </Col>
          </Row>
        </>
      )}
      <DocumentsDetails documents={invoice?.documents} />
    </>
  );
};

const PayInvoice = () => {
  const user = useSelector((state) => state.user);

  const [gateway, setGateway] = useState("");

  const { invoice, setInvoice, handlePayInvoice } =
    useContext(invoiceUserContext);

  const isInventory = user.wallet >= invoice.amount;

  const submitForm = () => {
    if (!isInventory && !gateway)
      return toastErrorMessage("انتخاب درگاه پرداخت الزامی است");

    handlePayInvoice(gateway);
  };
  return (
    <Modal
      show={!!invoice.isPay}
      className="modal-dialog-centered"
      size="lg"
      onHide={() => setInvoice({})}
    >
      <ModalHeader closeButton>
        <span className="fs-5 fw-bold">پرداخت فاکتور {invoice.number}</span>
      </ModalHeader>
      <ModalBody>
        <div className="alert alert-info bg-info-50 text-info-500 border-info-100 py-1 mt-1 text-sm radius-10">
          <div className="d-flex align-items-center gap-2">
            <Icon icon="solar:danger-triangle-linear" />
            <span>
              پرداخت فاکتور به مبلغ <ConvertCurrency price={invoice.amount} />{" "}
              <Currency />
            </span>
          </div>
        </div>
        {!isInventory && (
          <div className="col-12">
            <div className="mb-20">
              <PaymentGateway value={gateway} onChange={setGateway} />
            </div>
          </div>
        )}
        <div className="text-end">
          <button
            type="button"
            onClick={submitForm}
            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
          >
            پرداخت فاکتور
          </button>
        </div>
      </ModalBody>
    </Modal>
  );
};
